// R-feat-2026-10-17 U4 易用性:离线 mutation 队列
//
// 场景:断网时用户点击「保存」按钮,mutate 失败报错。
// 改为:把这次 mutation 放进 in-memory 队列,banner 提示「已排队」;
//     网络恢复后自动按顺序 retry(指数退避,避免雪崩)。
//
// 设计:
//   - Module-level 队列(useRef 持久化,setState 触发 UI 更新)
//   - 不持久化(sessionStorage/localStorage):浏览器关掉用户重新填更安全,
//     且避免 stale data 被错误 retry
//
// 用法:
//   import { enqueueOfflineRequest } from 'hooks/useOfflineQueue'
//   - enqueueOfflineRequest({ name, retry }) 入队
//   - flushOfflineQueue({ isOnline }) 在网络恢复时被 NetworkStatusBanner 调用

import { useState, useEffect, useCallback } from 'react'

let _queue = []  // [{ id, name, retry, createdAt, attempt }]
let _listeners = new Set()
let _retryInFlight = false

function _emit(evt) {
  _listeners.forEach(fn => {
    try { fn(evt) } catch (_) { /* listener 异常不阻断 */ }
  })
}

export function enqueueOfflineRequest({ name, retry }) {
  const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  _queue = [..._queue, { id, name, retry, createdAt: Date.now(), attempt: 0 }]
  _emit({ type: 'enqueue', queue: _queue })
  return id
}

export function cancelOfflineRequest(id) {
  _queue = _queue.filter(item => item.id !== id)
  _emit({ type: 'cancel', queue: _queue })
}

export function clearOfflineQueue() {
  _queue = []
  _emit({ type: 'clear', queue: _queue })
}

// 在线时调用,顺序 retry 队列里的请求(指数退避,避免雪崩)
export async function flushOfflineQueue({ maxAttempt = 3, isOnline } = {}) {
  if (_retryInFlight) return
  if (!_queue.length) return
  if (isOnline === false) return
  _retryInFlight = true
  try {
    const pending = [..._queue]
    const remaining = []
    for (const item of pending) {
      if (item.attempt >= maxAttempt) {
        _emit({ type: 'drop', id: item.id, name: item.name, reason: 'max_attempt' })
        continue
      }
      try {
        await item.retry()
        _emit({ type: 'success', id: item.id, name: item.name })
      } catch (e) {
        item.attempt += 1
        _emit({
          type: 'fail',
          id: item.id,
          name: item.name,
          attempt: item.attempt,
          error: e,
        })
        if (item.attempt < maxAttempt) {
          remaining.push(item)
        }
      }
      const backoffMs = 200 * Math.pow(3, item.attempt)
      await new Promise(r => setTimeout(r, backoffMs))
    }
    _queue = remaining
    _emit({ type: 'flush_done', queue: _queue })
  } finally {
    _retryInFlight = false
  }
}

// Hook 订阅队列变更(用于 UI 展示待重试项)
export function useOfflineQueue() {
  const [queue, setQueue] = useState(_queue)

  useEffect(() => {
    const listener = (evt) => setQueue([...(evt.queue || _queue)])
    _listeners.add(listener)
    return () => { _listeners.delete(listener) }
  }, [])

  const cancel = useCallback((id) => cancelOfflineRequest(id), [])
  const clear = useCallback(() => clearOfflineQueue(), [])
  const retryNow = useCallback(async () => {
    await flushOfflineQueue({ isOnline: true })
  }, [])

  return { queue, cancel, clear, retryNow }
}
